import React, { useContext, useEffect, useMemo, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { APP_VERSION } from '../config';
import { AlertTriangle, BookOpen, ShoppingBag, Wrench, MessageSquare, Play, ArrowRight, ClipboardList, History, Lightbulb, Target, Zap, CheckCircle, Calculator, Clock, Star, Users, FolderKanban, Settings, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import WorkspaceSetupBanner, { getWorkspaceStage, WorkspaceStatusLabel } from '@/components/WorkspaceSetupBanner';
import GatedToolCard from '@/components/GatedToolCard';

const readActiveContext = () => {
  try {
    const raw = localStorage.getItem('cnc:active_job_context');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// Helper function to format time ago
const getTimeAgo = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const Dashboard = () => {
  const { user, needsOnboarding, setShowOnboarding } = useContext(AuthContext);
  const token = localStorage.getItem('token');

  const [history, setHistory] = useState([]);
  const [savedSetups, setSavedSetups] = useState([]);
  const [recentMessages, setRecentMessages] = useState([]);
  const [chatRooms, setChatRooms] = useState([]);
  const [activeCtx, setActiveCtx] = useState(readActiveContext());
  const [machines, setMachines] = useState([]);
  const [setupProgress, setSetupProgress] = useState({ hasMachine: false, hasCollet: false, hasCalculation: false });
  const [onboardingData, setOnboardingData] = useState(null);
  
  // Phase 3.0: Track workspace setup completion state
  const [workspaceReady, setWorkspaceReady] = useState(false);
  const prevWorkspaceReady = useRef(false);

  useEffect(() => {
    const poll = setInterval(() => setActiveCtx(readActiveContext()), 1500);
    return () => clearInterval(poll);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!token) return;
        const [historyRes, machinesRes, savedRes, roomsRes, onboardingRes] = await Promise.all([
          axios.get(`${API}/calculations/history`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/machines`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/calculations/saved`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/community/rooms`),
          axios.get(`${API}/onboarding`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: null }))
        ]);
        setHistory(historyRes.data || []);
        setMachines(machinesRes.data || []);
        setSavedSetups(savedRes.data || []);
        setChatRooms(roomsRes.data || []);
        setOnboardingData(onboardingRes.data);
        
        // Fetch recent messages from first room if available
        if (roomsRes.data?.length > 0) {
          try {
            const messagesRes = await axios.get(`${API}/community/rooms/${roomsRes.data[0].id}/messages?limit=5`);
            setRecentMessages(messagesRes.data || []);
          } catch {
            // non-blocking
          }
        }
        
        // Calculate setup progress - more detailed machine profile check
        const hasMachine = machinesRes.data?.length > 0;
        const machine = machinesRes.data?.[0] || {};
        const hasCollet = Boolean(machine.collet_size);
        const hasSpindle = Boolean(machine.spindle_type_manual && machine.spindle_type_manual !== 'unknown');
        const hasRigidity = Boolean(machine.rigidity && machine.rigidity !== 'unknown');
        const hasController = Boolean(machine.controller_board && machine.controller_board !== 'unknown');
        const hasMotion = Boolean(machine.motor_class && machine.motor_class !== 'unknown');
        const hasCalculation = historyRes.data?.length > 0;
        
        // Calculate machine profile completeness (0-100%)
        const machineFields = [hasCollet, hasSpindle, hasRigidity, hasController, hasMotion];
        const machineProfilePercent = hasMachine 
          ? Math.round((machineFields.filter(Boolean).length / machineFields.length) * 100)
          : 0;
        
        setSetupProgress({ 
          hasMachine, 
          hasCollet, 
          hasSpindle,
          hasRigidity,
          hasController,
          hasMotion,
          hasCalculation,
          machineProfilePercent,
          machineName: machine.name || 'Unnamed Machine'
        });
        
        // Phase 3.0: Set workspace ready state
        setWorkspaceReady(hasMachine);
      } catch {
        // non-blocking
      }
    };

    fetchData();
  }, [token]);
  
  // Phase 3.1: Show toast when workspace becomes operational
  useEffect(() => {
    if (workspaceReady && !prevWorkspaceReady.current && setupProgress.machineProfilePercent >= 100) {
      toast.success('Machine validated. Workspace operational.', {
        duration: 4000,
        icon: <CheckCircle className="w-5 h-5 text-emerald-400" />
      });
    }
    prevWorkspaceReady.current = workspaceReady;
  }, [workspaceReady, setupProgress.machineProfilePercent]);

  const recommendations = useMemo(() => {
    const items = [];
    const last = history?.[0];
    const safety = activeCtx?.safety || 'safe';

    // Smart suggestions based on user progress
    if (!setupProgress.hasMachine) {
      // Priority: Set up machine first - point to wizard
      items.push({
        title: 'Set up your CNC machine',
        desc: 'Use our step-by-step wizard to configure your machine and get personalized recommendations.',
        link: '/setup-wizard',
        icon: Target,
        tone: 'primary'
      });
    } else if (setupProgress.machineProfilePercent < 100) {
      // Priority: Complete machine profile
      items.push({
        title: 'Complete your machine profile',
        desc: `Your machine is ${setupProgress.machineProfilePercent}% configured. Add more details for better calculations.`,
        link: '/setup-wizard',
        icon: Target,
        tone: 'primary'
      });
    } else {
      // Machine is set up - suggest calculator
      items.push({
        title: 'Calculate feeds & speeds',
        desc: last ? `Continue from your last setup: ${last.material} · ${last.tool_diameter}mm · ${last.flute_count}F.` : 'Start a new job setup with your machine profile.',
        link: '/calculator',
        icon: Play,
        tone: 'primary'
      });
    }

    // Quick Pick suggestion for new users
    if (!setupProgress.hasCalculation) {
      items.push({
        title: 'Find the right materials & tools',
        desc: 'Use Quick Pick to get instant recommendations based on your project type.',
        link: '/tools/quick-pick',
        icon: Lightbulb,
        tone: 'neutral'
      });
    }

    if (safety !== 'safe') {
      items.push({
        title: 'Troubleshoot finish risk',
        desc: 'Your last calculation raised caution flags. Run quick checks for burn, chatter, or tear-out.',
        link: '/troubleshooting',
        icon: AlertTriangle,
        tone: 'warn'
      });
    } else if (setupProgress.hasCalculation) {
      items.push({
        title: 'Export tool profile',
        desc: 'Generate a tool file for your CAM workflow and keep your library consistent.',
        link: '/tools/feeds-speeds',
        icon: ClipboardList,
        tone: 'neutral'
      });
    }

    items.push({
      title: 'Confirm material strategy',
      desc: 'Review best practices and common pitfalls for your current material and bit direction.',
      link: '/knowledge',
      icon: BookOpen,
      tone: 'neutral'
    });

    return items.slice(0, 3);
  }, [history, activeCtx, setupProgress]);

  // Setup progress indicator - enhanced with machine profile details
  const progressSteps = setupProgress.hasMachine ? [
    { label: 'Machine Added', done: setupProgress.hasMachine, link: '/setup-wizard' },
    { label: 'Spindle Type', done: setupProgress.hasSpindle, link: '/setup-wizard' },
    { label: 'Collet Size', done: setupProgress.hasCollet, link: '/settings/machine-setup' },
    { label: 'Rigidity', done: setupProgress.hasRigidity, link: '/settings/machine-setup' },
    { label: 'First Calculation', done: setupProgress.hasCalculation, link: '/calculator' },
  ] : [
    { label: 'Set Up Machine', done: false, link: '/setup-wizard', primary: true },
    { label: 'First Calculation', done: false, link: '/calculator' },
  ];
  const progressPercent = Math.round((progressSteps.filter(s => s.done).length / progressSteps.length) * 100);

  const coreTools = [
    {
      title: 'Troubleshooting',
      description: 'Step-by-step checks for common CNC routing problems.',
      icon: AlertTriangle,
      link: '/troubleshooting'
    },
    {
      title: 'Knowledge Base',
      description: 'Guides and best practices for safe, repeatable routing.',
      icon: BookOpen,
      link: '/knowledge'
    }
  ];

  const advancedTools = [
    {
      title: 'Project Tracker',
      description: 'Log and track your CNC projects with photos and notes.',
      icon: FolderKanban,
      link: '/projects'
    },
    {
      title: 'Marketplace',
      description: 'Buy/sell CNC items and connect with sellers.',
      icon: ShoppingBag,
      link: '/marketplace'
    },
    {
      title: 'My Equipment',
      description: 'Your machine profile and account settings.',
      icon: Wrench,
      link: '/profile'
    },
    {
      title: 'Community',
      description: 'Discuss routing workflows and practical shop tips.',
      icon: MessageSquare,
      link: '/community'
    }
  ];

  return (
    <div 
      data-testid="dashboard-page" 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6"
      style={bgStyle(IMAGES.ROUTER_DASHBOARD)}
    >
      {/* Dark Overlay */}
      <div className="fixed inset-0 bg-black/80 -z-10" style={{ pointerEvents: 'none' }} />

      {/* Page Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22] mb-2">Foreman Console</div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1
                className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#e67e22] tracking-tight uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                Work Session
              </h1>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-black/40 border border-[#3a3a3a] text-[#a3a3a3] tracking-wide self-center">
                v{APP_VERSION}
              </span>
            </div>
            <p className="mt-2 text-sm md:text-base text-[#e67e22] max-w-lg">
              Set up → validate → export → cut. Welcome back, {user?.full_name}.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Phase 3.1: Workspace Setup Warning Banner or Operational Badge */}
        <WorkspaceSetupBanner
          setupComplete={workspaceReady}
          completionPercent={setupProgress.machineProfilePercent || 0}
          setupLink="/setup-wizard"
          discipline="machine"
          userId={user?.email}
        />
        
        {/* Setup Progress Banner - Industrial Stage Display */}
        {workspaceReady && progressPercent < 100 && (
          <div className="bg-gradient-to-r from-[#e67e22]/20 to-[#e67e22]/5 backdrop-blur-sm border border-[#e67e22]/30 rounded-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-4">
                <Zap className="w-5 h-5 text-[#e67e22]" />
                <span className="text-white font-medium">
                  {setupProgress.machineName || 'Machine Setup'}
                </span>
                <WorkspaceStatusLabel percent={progressPercent} />
              </div>
              <div className="flex items-center gap-3">
                <Link to="/setup-wizard">
                  <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-black font-semibold h-7">
                    <ArrowRight className="w-3 h-3 mr-1" />
                    Continue Setup
                  </Button>
                </Link>
              </div>
            </div>
            <div className="w-full h-2 bg-black/70 rounded-full overflow-hidden mb-3">
              <div 
                className={`h-full bg-gradient-to-r from-[#e67e22] to-[#f39c12] transition-all duration-500 ${
                  progressPercent >= 100 ? 'shadow-[0_0_10px_rgba(16,185,129,0.5)]' : ''
                }`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="flex flex-wrap items-center gap-4">
              {progressSteps.map((step, i) => (
                <Link key={i} to={step.link} className="flex items-center gap-2 group">
                  {step.done ? (
                    <CheckCircle className="w-4 h-4 text-green-400" />
                  ) : step.primary ? (
                    <div className="w-4 h-4 border-2 border-emerald-500 rounded-full bg-emerald-500/20 group-hover:bg-emerald-500/40 animate-pulse" />
                  ) : (
                    <div className="w-4 h-4 border-2 border-[#3a3a3a] rounded-full group-hover:border-[#e67e22]" />
                  )}
                  <span className={`text-sm ${
                    step.done 
                      ? 'text-green-400 line-through' 
                      : step.primary 
                        ? 'text-emerald-400 font-medium group-hover:text-emerald-300' 
                        : 'text-[#d6d6d6] group-hover:text-[#e67e22]'
                  }`}>
                    {step.label}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Active Profile Widget - Shows after onboarding is complete */}
        {onboardingData?.completed && (
          <div className="bg-black/70 backdrop-blur-sm border border-[#3a3a3a]/50 rounded-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#e67e22]" />
                <span className="text-white font-medium">Active Profile</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowOnboarding?.(true)}
                className="text-[#a3a3a3] hover:text-white h-7 px-2"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Update Setup
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <div className="bg-black/60 rounded p-2">
                <div className="text-xs text-[#b8b8b8]">Units</div>
                <div className="text-sm text-white font-medium">
                  {onboardingData.units === 'metric' ? 'Metric (mm)' : 'Imperial (in)'}
                </div>
              </div>
              <div className="bg-black/60 rounded p-2">
                <div className="text-xs text-[#b8b8b8]">Experience</div>
                <div className="text-sm text-white font-medium capitalize">{onboardingData.experience_level}</div>
              </div>
              <div className="bg-black/60 rounded p-2">
                <div className="text-xs text-[#b8b8b8]">Machine</div>
                <div className="text-sm text-white font-medium capitalize">{onboardingData.machine_class}</div>
              </div>
              <div className="bg-black/60 rounded p-2">
                <div className="text-xs text-[#b8b8b8]">Spindle</div>
                <div className="text-sm text-white font-medium">
                  {onboardingData.spindle_type === 'trim_router' ? 'Trim Router' : 'VFD Spindle'}
                </div>
              </div>
              <div className="bg-black/60 rounded p-2">
                <div className="text-xs text-[#b8b8b8]">Max RPM</div>
                <div className="text-sm text-white font-medium">{onboardingData.max_rpm?.toLocaleString()}</div>
              </div>
              <div className="bg-black/60 rounded p-2">
                <div className="text-xs text-[#b8b8b8]">Safety</div>
                <div className="text-sm text-white font-medium capitalize">{onboardingData.safety_style}</div>
              </div>
            </div>
            {/* Last calculation context */}
            {activeCtx && (
              <div className="mt-3 pt-3 border-t border-[#3a3a3a]">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-[#a3a3a3]">
                    Last job: <span className="text-white">{activeCtx.material?.replace('_', ' ')}</span>
                    {activeCtx.tool_diameter && <span className="text-[#e67e22]"> • {activeCtx.tool_diameter}mm {activeCtx.flute_count}F</span>}
                  </div>
                  <Link 
                    to="/calculator" 
                    className="text-xs text-[#e67e22] hover:text-[#f39c12] flex items-center gap-1"
                  >
                    Resume <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Prompt to complete onboarding if skipped */}
        {needsOnboarding && !onboardingData?.completed && (
          <div className="bg-gradient-to-r from-blue-500/20 to-blue-500/5 backdrop-blur-sm border border-blue-500/30 rounded-sm p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Settings className="w-5 h-5 text-blue-400" />
                <div>
                  <div className="text-white font-medium">Complete Your Setup</div>
                  <div className="text-xs text-[#a3a3a3]">Get personalized calculations tailored to your machine</div>
                </div>
              </div>
              <Button
                onClick={() => setShowOnboarding?.(true)}
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                Start Setup
              </Button>
            </div>
          </div>
        )}

        {/* Core tools - Transparent background */}
        <div className="bg-black/70 backdrop-blur-sm border border-[#3a3a3a]/50 rounded-sm p-6">
          <div className="mb-4">
            <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22]">Core tools</div>
            <div className="text-sm text-[#e0e0e0] mt-1">
              Operational tools used before and after a cut.
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {/* Troubleshooting - Left - Gated */}
            <GatedToolCard
              to="/troubleshooting"
              setupComplete={workspaceReady}
              className="bg-black/60 border border-[#3a3a3a]/50 p-4 rounded-sm hover:border-[#e67e22]/50 transition-all group"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-black/60 border border-[#3a3a3a]/50 flex items-center justify-center shrink-0 group-hover:border-[#e67e22]/50 transition-colors">
                  <AlertTriangle className="w-5 h-5 text-[#e67e22]" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#e67e22] group-hover:text-[#e67e22] transition-colors">
                    Troubleshooting
                  </div>
                  <div className="text-xs text-[#e0e0e0] mt-1">
                    Step-by-step checks for common CNC routing problems.
                  </div>
                </div>
              </div>
            </GatedToolCard>
            
            {/* Empty middle space */}
            <div className="hidden md:block"></div>
            
            {/* Knowledge Base - Right - Not gated (educational) */}
            <Link
              to="/knowledge"
              className="bg-black/60 border border-[#3a3a3a]/50 p-4 rounded-sm hover:border-[#e67e22]/50 transition-all group"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-black/60 border border-[#3a3a3a]/50 flex items-center justify-center shrink-0 group-hover:border-[#e67e22]/50 transition-colors">
                  <BookOpen className="w-5 h-5 text-[#e67e22]" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#e67e22] group-hover:text-[#e67e22] transition-colors">
                    Knowledge Base
                  </div>
                  <div className="text-xs text-[#e0e0e0] mt-1">
                    Guides and best practices for safe, repeatable routing.
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* Support & advanced - Transparent background */}
        <div className="bg-black/70 backdrop-blur-sm border border-[#3a3a3a]/50 rounded-sm p-6">
          <div className="mb-4">
            <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22]">Support & advanced</div>
            <div className="text-sm text-[#e0e0e0] mt-1">
              Secondary tools for sourcing, equipment, and discussion.
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {advancedTools.map((tool, index) => {
              const Icon = tool.icon;
              return (
                <Link
                  key={index}
                  to={tool.link}
                  className="bg-black/60 border border-[#3a3a3a]/50 p-4 rounded-sm hover:border-[#e67e22]/50 transition-all group"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-black/60 border border-[#3a3a3a]/50 flex items-center justify-center shrink-0 group-hover:border-[#e67e22]/50 transition-colors">
                      <Icon className="w-5 h-5 text-[#e67e22]" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-white group-hover:text-[#e67e22] transition-colors">
                        {tool.title}
                      </div>
                      <div className="text-xs text-[#e0e0e0] mt-1">
                        {tool.description}
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Recent Activity Feed */}
        <div className="bg-black/70 backdrop-blur-sm border border-[#3a3a3a]/50 rounded-sm p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-[#e67e22] flex items-center gap-2">
                <History className="w-4 h-4" />
                Recent Activity
              </div>
              <div className="text-sm text-[#e0e0e0] mt-1">
                Pick up where you left off
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Recent Calculations */}
            <div className="bg-black/70 border border-[#3a3a3a]/30 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <Calculator className="w-4 h-4 text-[#3b82f6]" />
                <span className="text-sm font-medium text-white">Recent Calculations</span>
              </div>
              {history.length > 0 ? (
                <div className="space-y-2">
                  {history.slice(0, 3).map((calc, i) => {
                    const materialName = calc.material?.replace('_', ' ').replace(/^\w/, c => c.toUpperCase()) || 'Unknown';
                    const timeAgo = getTimeAgo(calc.created_at);
                    return (
                      <Link
                        key={i}
                        to="/tools/feeds-speeds"
                        onClick={() => {
                          localStorage.setItem('cnc:last_calculation', JSON.stringify({
                            formData: {
                              material: calc.material,
                              tool_diameter: calc.tool_diameter,
                              flute_count: calc.flute_count,
                              tool_type: calc.tool_type || 'end_mill',
                              bit_direction: calc.bit_direction || 'upcut',
                              spindle_type: calc.spindle_type || 'medium_spindle',
                            },
                            unitSystem: calc.unit_system || 'mm'
                          }));
                        }}
                        className="block p-2 bg-black/20 rounded hover:bg-[#3b82f6]/10 hover:border-[#3b82f6]/30 border border-transparent transition-all"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-xs font-medium text-white truncate">{materialName}</div>
                            <div className="text-xs text-[#a3a3a3]">{calc.tool_diameter}mm · {calc.flute_count}F</div>
                          </div>
                          <div className="text-xs text-[#6b7280] flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {timeAgo}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="text-xs text-[#6b7280] py-4 text-center">
                  No calculations yet.{' '}
                  <Link to="/tools/feeds-speeds" className="text-[#3b82f6] hover:underline">Start one</Link>
                </div>
              )}
            </div>

            {/* Saved Setups */}
            <div className="bg-black/70 border border-[#3a3a3a]/30 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <Star className="w-4 h-4 text-[#f59e0b]" />
                <span className="text-sm font-medium text-white">Saved Setups</span>
              </div>
              {savedSetups.length > 0 ? (
                <div className="space-y-2">
                  {savedSetups.slice(0, 3).map((setup, i) => {
                    const materialName = setup.material?.replace('_', ' ').replace(/^\w/, c => c.toUpperCase()) || 'Unknown';
                    return (
                      <Link
                        key={i}
                        to="/tools/feeds-speeds"
                        onClick={() => {
                          localStorage.setItem('cnc:last_calculation', JSON.stringify({
                            formData: {
                              material: setup.material,
                              tool_diameter: setup.tool_diameter,
                              flute_count: setup.flute_count,
                              tool_type: setup.tool_type || 'end_mill',
                              bit_direction: setup.bit_direction || 'upcut',
                              spindle_type: setup.spindle_type || 'medium_spindle',
                            },
                            unitSystem: setup.unit_system || 'mm'
                          }));
                        }}
                        className="block p-2 bg-black/20 rounded hover:bg-[#f59e0b]/10 hover:border-[#f59e0b]/30 border border-transparent transition-all"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-xs font-medium text-[#f59e0b] truncate">{setup.name || materialName}</div>
                            <div className="text-xs text-[#a3a3a3]">{setup.tool_diameter}mm · {setup.spindle_rpm} RPM</div>
                          </div>
                          <Star className="w-3 h-3 text-[#f59e0b]" />
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="text-xs text-[#6b7280] py-4 text-center">
                  No saved setups yet. Save a calculation to quick-load it later.
                </div>
              )}
            </div>

            {/* Community Messages */}
            <div className="bg-black/70 border border-[#3a3a3a]/30 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-[#10b981]" />
                <span className="text-sm font-medium text-white">Community</span>
              </div>
              {recentMessages.length > 0 ? (
                <div className="space-y-2">
                  {recentMessages.slice(0, 3).map((msg, i) => {
                    const timeAgo = getTimeAgo(msg.timestamp);
                    return (
                      <Link
                        key={i}
                        to="/community"
                        className="block p-2 bg-black/20 rounded hover:bg-[#10b981]/10 hover:border-[#10b981]/30 border border-transparent transition-all"
                      >
                        <div className="text-xs font-medium text-[#10b981] truncate">{msg.user_name}</div>
                        <div className="text-xs text-[#a3a3a3] line-clamp-1">{msg.message}</div>
                        <div className="text-xs text-[#6b7280] mt-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {timeAgo}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="text-xs text-[#6b7280] py-4 text-center">
                  No recent messages.{' '}
                  <Link to="/community" className="text-[#10b981] hover:underline">Join the discussion</Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Status Bar - Transparent */}
      <div className="bg-black/70 backdrop-blur-sm border border-[#3a3a3a]/50 rounded-sm p-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-[#27ae60] rounded-full animate-pulse" />
              <span className="text-xs text-[#d6d6d6]">System online</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-[#27ae60] rounded-full" />
              <span className="text-xs text-[#d6d6d6]">Database connected</span>
            </div>
          </div>
          <div className="text-xs font-mono text-[#a3a3a3]">CNC HELPDESK</div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
